/**
 * GalleryMode Module
 * Handles gallery navigation for static photo viewing (non-360° variants)
 */

export type SlideDirection = 'next' | 'prev' | 'none';

export interface GalleryConfig {
  frameCount: number;
  thumbnailSize?: number;  // default: 64px
  swipeThreshold?: number; // default: 50px
  onFrameChange: (frame: number, direction: SlideDirection) => void;
  onSwipeStart?: () => void;
  onSwipeEnd?: () => void;
}

interface GalleryState {
  currentFrame: number;
  isSwiping: boolean;
  touchStartX: number;
  touchStartY: number;
}

export class GalleryMode {
  private state: GalleryState;
  private config: GalleryConfig;
  private container: HTMLElement | null = null;
  private enabled: boolean = true;

  // Bound handlers for cleanup
  private boundHandlers: {
    touchStart: (e: TouchEvent) => void;
    touchMove: (e: TouchEvent) => void;
    touchEnd: (e: TouchEvent) => void;
    keydown: (e: KeyboardEvent) => void;
  };

  constructor(config: GalleryConfig) {
    this.config = {
      thumbnailSize: 64,
      swipeThreshold: 50,
      ...config,
    };

    this.state = {
      currentFrame: 0,
      isSwiping: false,
      touchStartX: 0,
      touchStartY: 0,
    };

    // Bind handlers
    this.boundHandlers = {
      touchStart: this.handleTouchStart.bind(this),
      touchMove: this.handleTouchMove.bind(this),
      touchEnd: this.handleTouchEnd.bind(this),
      keydown: this.handleKeydown.bind(this),
    };
  }

  /**
   * Attach to container element
   */
  attach(container: HTMLElement): void {
    this.container = container;

    // Touch events for swipe
    container.addEventListener('touchstart', this.boundHandlers.touchStart, { passive: true });
    container.addEventListener('touchmove', this.boundHandlers.touchMove, { passive: false });
    container.addEventListener('touchend', this.boundHandlers.touchEnd, { passive: true });

    // Keyboard events
    document.addEventListener('keydown', this.boundHandlers.keydown);
  }

  /**
   * Detach from container
   */
  detach(): void {
    if (this.container) {
      this.container.removeEventListener('touchstart', this.boundHandlers.touchStart);
      this.container.removeEventListener('touchmove', this.boundHandlers.touchMove);
      this.container.removeEventListener('touchend', this.boundHandlers.touchEnd);
    }
    document.removeEventListener('keydown', this.boundHandlers.keydown);
    this.container = null;
  }

  /**
   * Enable/disable gallery navigation
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /**
   * Set current frame with direction detection
   */
  setFrame(frame: number, forceDirection?: SlideDirection): void {
    // Block if disabled (animation in progress)
    if (!this.enabled) return;

    // Clamp frame to valid range
    const newFrame = Math.max(0, Math.min(frame, this.config.frameCount - 1));

    if (newFrame !== this.state.currentFrame) {
      // Determine slide direction
      const direction: SlideDirection = forceDirection ??
        (newFrame > this.state.currentFrame ? 'next' : 'prev');

      this.state.currentFrame = newFrame;
      this.config.onFrameChange(newFrame, direction);
    }
  }

  /**
   * Go to next frame
   */
  goNext(): void {
    if (!this.enabled) return;
    if (this.state.currentFrame < this.config.frameCount - 1) {
      this.setFrame(this.state.currentFrame + 1, 'next');
    }
  }

  /**
   * Go to previous frame
   */
  goPrev(): void {
    if (!this.enabled) return;
    if (this.state.currentFrame > 0) {
      this.setFrame(this.state.currentFrame - 1, 'prev');
    }
  }

  /**
   * Get current frame
   */
  getFrame(): number {
    return this.state.currentFrame;
  }

  /**
   * Update configuration
   */
  updateConfig(updates: Partial<GalleryConfig>): void {
    this.config = { ...this.config, ...updates };
  }

  /**
   * Check if can go next
   */
  canGoNext(): boolean {
    return this.state.currentFrame < this.config.frameCount - 1;
  }

  /**
   * Check if can go previous
   */
  canGoPrev(): boolean {
    return this.state.currentFrame > 0;
  }

  // ==========================================
  // Touch/Swipe Handlers
  // ==========================================

  private handleTouchStart(e: TouchEvent): void {
    if (!this.enabled || e.touches.length !== 1) return;

    this.state.isSwiping = true;
    this.state.touchStartX = e.touches[0].clientX;
    this.state.touchStartY = e.touches[0].clientY;
    this.config.onSwipeStart?.();
  }

  private handleTouchMove(e: TouchEvent): void {
    if (!this.enabled || !this.state.isSwiping) return;

    // Calculate deltas
    const deltaX = e.touches[0].clientX - this.state.touchStartX;
    const deltaY = e.touches[0].clientY - this.state.touchStartY;

    // If horizontal swipe is dominant, prevent vertical scroll
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      e.preventDefault();
    }
  }

  private handleTouchEnd(e: TouchEvent): void {
    if (!this.enabled || !this.state.isSwiping) return;

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - this.state.touchStartX;
    const deltaY = touch.clientY - this.state.touchStartY;

    // Only trigger if horizontal swipe is dominant
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      if (deltaX < -this.config.swipeThreshold!) {
        // Swipe left -> next (slide from right)
        this.goNext();
      } else if (deltaX > this.config.swipeThreshold!) {
        // Swipe right -> prev (slide from left)
        this.goPrev();
      }
    }

    this.state.isSwiping = false;
    this.config.onSwipeEnd?.();
  }

  // ==========================================
  // Keyboard Handler
  // ==========================================

  private handleKeydown(e: KeyboardEvent): void {
    if (!this.enabled) return;

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        this.goPrev();
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.goNext();
        break;
    }
  }

  /**
   * Cleanup
   */
  destroy(): void {
    this.detach();
  }
}
